import React from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import colors from "../../assets/theme/colors";
import {
  getImportBatchDateLabel,
  getImportBatchFileCount,
  getImportBatchStatusLabel,
  getImportBatchSuccessfulFileCount,
} from "./importHistoryHistoryModel";

const ImportHistoryBatchHistory = ({ batches = [], onResumeBatch = null, resumeDisabled = false }) => {
  const navigate = useNavigate();

  return (
    <Box component="section" aria-labelledby="past-imports-title">
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.25 }}>
        <Box>
          <Typography id="past-imports-title" sx={{ fontSize: "1.15rem", fontWeight: 900, color: colors.semantic.ink }}>Past imports</Typography>
          <Typography color="text.secondary" sx={{ fontSize: "0.86rem" }}>Every imported document also remains in Profile → Library → Documents.</Typography>
        </Box>
        <Button onClick={() => navigate("/templates?tab=documents")} sx={{ textTransform: "none", fontWeight: 800 }}>Open Documents</Button>
      </Stack>

      {!batches.length ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography color="text.secondary">Completed and archived imports will appear here.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {batches.map((batch) => {
            const fileCount = getImportBatchFileCount(batch);
            const successfulCount = getImportBatchSuccessfulFileCount(batch);
            const canResume = !["reviewed", "dismissed"].includes(batch.status);
            return (
              <Paper key={batch.id} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                      <Typography sx={{ fontWeight: 850 }}>Imported {getImportBatchDateLabel(batch)}</Typography>
                      <Chip size="small" label={getImportBatchStatusLabel(batch)} />
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.25, fontSize: "0.82rem" }}>{fileCount} {fileCount === 1 ? "document" : "documents"} · {successfulCount} preserved</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Stack spacing={0.7}>
                    {(batch.items || []).map((item) => (
                      <Stack key={item.key} direction="row" justifyContent="space-between" spacing={1}>
                        <Typography noWrap sx={{ minWidth: 0, fontSize: "0.86rem" }}>{item.fileName}</Typography>
                        <Typography color={item.uploadStatus === "failed" ? "error" : "text.secondary"} sx={{ flexShrink: 0, fontSize: "0.8rem" }}>{item.uploadStatus === "failed" ? "Upload failed" : item.sourceNoteId ? "Preserved" : "Not uploaded"}</Typography>
                      </Stack>
                    ))}
                    <Button onClick={() => navigate("/templates?tab=documents")} sx={{ alignSelf: "flex-start", px: 0, textTransform: "none", fontWeight: 800 }}>View these documents in Library</Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              {canResume && onResumeBatch ? (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Button fullWidth variant="contained" disabled={resumeDisabled} onClick={() => onResumeBatch(batch)} sx={{ textTransform: "none", fontWeight: 850 }}>
                    {batch.status === "processing" || batch.status === "uploading" ? "View progress" : "Continue reviewing"}
                  </Button>
                </Box>
              ) : null}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default ImportHistoryBatchHistory;
